// ContactImport.tsx · v1.2 · v12.10.21 (#1950 a Preferred language column - auto-matched by name, Spanish / Español / es stored as the code;
//   #1951 a Caller type column matched to the practice's caller types - with no column (or empty cells) Step 1 asks for ONE caller type
//   for the file before Check; D6 Phone 4; the template gets Caller type + Preferred language) · 2026-10-26 · VoiceSpectrum AI
// ContactImport.tsx · v1.1 · v12.10.17.1 (#1787 rows imported with a value left out are listed; #1790 the review row labels its External ID) · 2026-10-16 · VoiceSpectrum AI
// ContactImport.tsx · v1.0 · v12.10.17 (#1746 T123 Import patients into the contact book) · 2026-10-16 · VoiceSpectrum AI
//
// Contacts › Import - the locked prototype UI-PROTO-CONTACTS-IMPORT-T123 v1.2 (CONTACTS-DIGIDMS-IMPORT v1.0 I1-I14):
//   Step 1 of 2  Where the file comes from (Source, a suggestion: DigiDMS) · The file (.xlsx or .csv, read HERE in the
//                browser) · Match the columns: Field in VoiceSpectrum | Column in your file | Phone type | First row,
//                matched by the column names, the phone type read from the name ("Mobile phone" -> Mobile).
//                "Download template" gives a .csv with our column names (External ID - never "Patient ID / MRN").
//   Step 2 of 2  Check before importing - the server's dry run: New · Matched - will be updated · New on a shared
//                phone · Needs review (skipped, "Download these rows"). Nothing is written.
//   Import       a typed IMPORT confirm (the ConfirmDelete pattern), then the write. Manual only - no sync.
import { useEffect, useMemo, useRef, useState } from 'react';
import api, { ApiError } from '../../lib/api';
import { toast } from '../../lib/toast';
import { maskPhone } from '../../lib/phone';
import { readSheetFile, csvLine, serialToDate, type Sheet } from '../../lib/sheetFile';

type FieldDef = { token: string; label: string; type: string; section?: string };
type PhoneType = 'mobile' | 'home' | 'work' | 'landline' | 'other';
export const PHONE_TYPES: { value: PhoneType; label: string }[] = [
  { value: 'mobile', label: 'Mobile' }, { value: 'home', label: 'Home' }, { value: 'work', label: 'Work' },
  { value: 'landline', label: 'Landline' }, { value: 'other', label: 'Other' },
];
type Slot = 1 | 2 | 3 | 4;   // v12.10.21 (D6): Phone 1-4
const SLOTS: Slot[] = [1, 2, 3, 4];
type Target = { key: string; label: string; phone?: Slot; custom?: FieldDef };
/** v12.10.21 (#1951): the practice's caller types (Contacts › options). */
type CallerTypeOpt = { key: string; name: string; enabled?: boolean };
type PlanRow = { row: number; outcome: 'new' | 'matched' | 'shared' | 'review'; rule?: string | null; contactId?: number | null; systemId?: string | null;
  name: string; oldName?: string | null; externalId?: string | null; phone?: string | null; reason?: string | null; changes?: string[] | null; dropped?: string[] | null };
type Counts = { new: number; matched: number; shared: number; review: number };
type Plan = { total: number; counts: Counts; rows: PlanRow[]; dropped?: number };
type RowDto = { row: number; externalId: string | null; firstName: string | null; lastName: string | null; dob: string | null; zip: string | null;
  phones: { number: string | null; type: string | null }[]; email: string | null; custom: Record<string, string> | null;
  callerType: string | null; preferredLanguage: string | null };   // v12.10.21 (#1950 #1951): the cells as typed - the server reads them against the practice's lists

const FIXED: Target[] = [
  { key: 'externalId', label: 'External ID' }, { key: 'firstName', label: 'First name' }, { key: 'lastName', label: 'Last name' },
  { key: 'dob', label: 'Date of birth' }, { key: 'zip', label: 'ZIP code' },
  { key: 'phone1', label: 'Phone 1', phone: 1 }, { key: 'phone2', label: 'Phone 2', phone: 2 }, { key: 'phone3', label: 'Phone 3', phone: 3 },
  { key: 'phone4', label: 'Phone 4', phone: 4 },   // v12.10.21 (D6)
  { key: 'email', label: 'Email' },
  { key: 'preferredLanguage', label: 'Preferred language' },   // v12.10.21 (#1950)
  { key: 'callerType', label: 'Caller type' },                 // v12.10.21 (#1951)
];
const NOT_IN_FILE = -1;
const SHOW = 5;
const nf = (n: number) => n.toLocaleString('en-US');
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/** The phone type a column name says ("Mobile phone" -> mobile); null when it says nothing. Same rule as the server. */
export function guessPhoneType(header: string): PhoneType | null {
  const h = header.toLowerCase();
  if (h.includes('mobile') || h.includes('cell')) return 'mobile';
  if (h.includes('home')) return 'home';
  if (h.includes('work') || h.includes('office') || h.includes('business')) return 'work';
  if (h.includes('landline') || h.includes('land line')) return 'landline';
  return null;
}

/** Columns matched by their names; each column is used once. Exported for the check that the template maps to itself. */
export function autoMap(headers: string[], targets: Target[]): Record<string, number> {
  const used = new Set<number>();
  const map: Record<string, number> = {};
  const pick = (key: string, test: (h: string, raw: string) => boolean) => {
    if (map[key] !== undefined) return;
    const i = headers.findIndex((raw, idx) => !used.has(idx) && test(norm(raw), raw));
    if (i >= 0) { map[key] = i; used.add(i); }
  };
  pick('externalId', (h) => ['externalid', 'patientid', 'mrn', 'patientidmrn', 'patientno', 'patientnumber', 'accountnumber', 'accountno', 'chartnumber', 'chartno'].includes(h));
  pick('firstName', (h) => ['firstname', 'first', 'fname', 'givenname', 'patientfirstname'].includes(h));
  pick('lastName', (h) => ['lastname', 'last', 'lname', 'surname', 'familyname', 'patientlastname'].includes(h));
  pick('dob', (h) => ['dateofbirth', 'dob', 'birthdate', 'birthday', 'patientdob'].includes(h));
  pick('zip', (h) => ['zip', 'zipcode', 'postalcode', 'zip5', 'postcode'].includes(h));
  pick('email', (h) => h.includes('email'));
  // v12.10.21 (#1950 #1951): Preferred language / Caller type by their names (DigiDMS: "Language")
  pick('preferredLanguage', (h) => ['preferredlanguage', 'language', 'lang', 'primarylanguage', 'patientlanguage', 'spokenlanguage', 'languagepreference'].includes(h));
  pick('callerType', (h) => ['callertype', 'callerkind', 'contacttype', 'typeofcaller'].includes(h));
  for (const s of SLOTS) pick(`phone${s}`, (h) => h === `phone${s}` || h === `phone${s}number`);
  for (const s of SLOTS) pick(`phone${s}`, (h, raw) => /phone|mobile|cell|tel/.test(h) && !/type/.test(h) && (guessPhoneType(raw) !== null || /phone|tel/.test(h)));
  for (const t of targets.filter((x) => x.custom)) pick(t.key, (h) => h === norm(t.label) || h === norm(t.custom!.token.split('.').pop() ?? '') || (norm(t.label).length > 3 && h.startsWith(norm(t.label))));
  return map;
}

function phoneTypesFor(headers: string[], map: Record<string, number>): Record<Slot, PhoneType> {
  const def: PhoneType[] = ['mobile', 'home', 'work', 'other'];
  const o = {} as Record<Slot, PhoneType>;
  for (const s of SLOTS) {
    const i = map[`phone${s}`];
    o[s] = (i !== undefined && i >= 0 ? guessPhoneType(headers[i]) : null) ?? def[s - 1];
  }
  return o;
}

/** A date cell an Excel export left as a day number (25996) is read as the date it is. */
function dobText(v: string): string {
  const t = v.trim();
  if (/^\d{4,5}(\.\d+)?$/.test(t) && Number(t) > 366 && Number(t) < 80000) return serialToDate(Number(t));
  return t;
}

function download(name: string, text: string) {
  const blob = new Blob(['﻿' + text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function errText(e: unknown, fallback: string): string {
  if (e instanceof ApiError && e.status === 403) return 'you don’t have access to import contacts - it needs Contacts › Add and edit';
  if (e instanceof ApiError) { const b = e.body as { message?: string } | null; if (b?.message) return b.message; }
  return fallback;
}

const IUp = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 15V3" /><path d="m7 8 5-5 5 5" /><path d="M5 21h14" /></svg>;
const IDown = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" /></svg>;
const IX = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>;

export default function ImportDrawer({ customFields, callerTypes = [], onClose, onDone }: { customFields: FieldDef[]; callerTypes?: CallerTypeOpt[]; onClose: () => void; onDone: () => void }) {
  const targets: Target[] = useMemo(() => [...FIXED, ...customFields.map((f) => ({ key: 'c:' + f.token, label: f.label, custom: f }))], [customFields]);
  const [source, setSource] = useState('DigiDMS');
  const [file, setFile] = useState<{ name: string; sheet: Sheet } | null>(null);
  const [fileErr, setFileErr] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [map, setMap] = useState<Record<string, number>>({});
  const [ptype, setPtype] = useState<Record<Slot, PhoneType>>({ 1: 'mobile', 2: 'home', 3: 'work', 4: 'other' });
  const [fileType, setFileType] = useState('');   // v12.10.21 (#1951): the ONE caller type for the file (no column / empty cells)
  const [plan, setPlan] = useState<Plan | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Esc closes (the confirm handles its own Esc first)
  useEffect(() => {
    function onKey(ev: KeyboardEvent) { if (ev.key === 'Escape' && !busy && !confirm && !ev.defaultPrevented) { ev.preventDefault(); onClose(); } }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  async function choose(f: File | undefined | null) {
    if (!f) return;
    setReading(true); setFileErr(null); setPlan(null);
    try {
      const sheet = await readSheetFile(f);
      if (sheet.headers.length === 0 || sheet.rows.length === 0) throw new Error('The file needs a header row and at least one row of patients.');
      const m = autoMap(sheet.headers, targets);
      setFile({ name: f.name, sheet }); setMap(m); setPtype(phoneTypesFor(sheet.headers, m));
    } catch (e) {
      setFile(null); setFileErr(e instanceof Error ? e.message : 'The file could not be read.');
    } finally { setReading(false); if (inputRef.current) inputRef.current.value = ''; }
  }

  function template() {
    // v12.10.21 (#1950 #1951 D6): + Other phone (Phone 4), Preferred language, Caller type
    const head = ['External ID', 'First name', 'Last name', 'Date of birth', 'ZIP code', 'Mobile phone', 'Home phone', 'Work phone', 'Other phone', 'Email',
      'Preferred language', 'Caller type', ...customFields.map((f) => f.label)];
    download('VoiceSpectrum_contacts_template.csv', csvLine(head) + '\r\n');
  }

  const col = (key: string) => (map[key] ?? NOT_IN_FILE);
  const usedCols = new Set(Object.values(map).filter((i) => i >= 0));
  const matchedCount = targets.filter((t) => col(t.key) >= 0).length;
  // v12.10.21 (#1951 C2): no Caller type column, or a row that leaves it empty -> ONE caller type must be picked for the file before Check
  const ctCol = col('callerType');
  const needsFileType = !!file && (ctCol < 0 || file.sheet.rows.some((r) => !(r[ctCol] ?? '').trim()));
  const typeOptions = callerTypes.filter((t) => t.enabled !== false);

  function rowsDto(): RowDto[] {
    if (!file) return [];
    const v = (r: string[], key: string) => { const i = col(key); const s = i >= 0 ? (r[i] ?? '').trim() : ''; return s === '' ? null : s; };
    return file.sheet.rows.map((r, idx) => {
      const custom: Record<string, string> = {};
      for (const t of targets.filter((x) => x.custom)) { const s = v(r, t.key); if (s) custom[t.custom!.token] = t.custom!.type === 'date' ? dobText(s) : s; }
      const dob = v(r, 'dob');
      return {
        row: file.sheet.rowNos[idx] ?? idx + 2, externalId: v(r, 'externalId'), firstName: v(r, 'firstName'), lastName: v(r, 'lastName'),
        dob: dob ? dobText(dob) : null, zip: v(r, 'zip'),
        phones: SLOTS.map((s) => ({ number: v(r, `phone${s}`), type: ptype[s] })),
        email: v(r, 'email'), custom: Object.keys(custom).length ? custom : null,
        callerType: v(r, 'callerType'), preferredLanguage: v(r, 'preferredLanguage'),   // v12.10.21 (#1950 #1951)
      };
    });
  }

  async function check() {
    if (!file) return;
    if (!source.trim()) { toast('error', 'Say where the file comes from', 'Source - for example DigiDMS.'); return; }
    if (needsFileType && !fileType) { toast('error', 'Pick the caller type for this file', 'The file has no Caller type column, or a row leaves it empty.'); return; }
    setBusy(true);
    try {
      const p = await api.post<Plan>('/t2/contacts/import/preview', { source: source.trim(), fileName: file.name, rows: rowsDto(), callerType: fileType || null });
      setPlan(p);
    } catch (e) { toast('error', 'The file was not checked', errText(e, 'The server did not answer - nothing was written.')); }
    finally { setBusy(false); }
  }

  async function doImport() {
    if (!file || !plan) return;
    setBusy(true);
    try {
      const r = await api.post<{ created: number; updated: number; skipped: number }>('/t2/contacts/import',
        { source: source.trim(), fileName: file.name, rows: rowsDto(), expect: plan.counts, callerType: fileType || null });
      toast('success', 'Import done.', `${nf(r.created)} added · ${nf(r.updated)} updated${r.skipped ? ` · ${nf(r.skipped)} skipped` : ''} - every change is in each contact's History.`);
      setConfirm(false); onDone();
    } catch (e) {
      const b = e instanceof ApiError && e.status === 409 ? (e.body as { reason?: string } | null) : null;
      toast('error', 'Nothing was imported', errText(e, 'The server did not answer - nothing was written.'));
      setConfirm(false);
      if (b?.reason === 'changed') void check();   // the book changed since the check - show the new check
    } finally { setBusy(false); }
  }

  function reviewCsv() {
    if (!file || !plan) return;
    const bad = plan.rows.filter((r) => r.outcome === 'review');
    const lines = [csvLine([...file.sheet.headers, 'Why it was not imported'])];
    const at = new Map(file.sheet.rowNos.map((n, i) => [n, i] as const));
    for (const r of bad) lines.push(csvLine([...(file.sheet.rows[at.get(r.row) ?? -1] ?? []), r.reason ?? '']));
    const base = file.name.replace(/\.[^.]+$/, '');
    download(`${base}_needs_review.csv`, lines.join('\r\n') + '\r\n');
  }

  const toImport = plan ? plan.total - plan.counts.review : 0;
  const first = file?.sheet.rows[0] ?? [];
  const list = (o: PlanRow['outcome']) => (plan?.rows ?? []).filter((r) => r.outcome === o);
  const label = source.trim() || 'the file';

  return (
    <>
      <div className="dscrim" onClick={busy ? undefined : onClose} />
      <aside className="drawer cim" role="dialog" aria-modal="true" aria-label="Import patients">
        <div className="dnav">
          <span className="refc">Import patients</span>
          <span className="sp" />
          <button type="button" className="xbtn" onClick={onClose} disabled={busy} aria-label="Close" title="Close (Esc)"><IX /></button>
        </div>
        <div className="dbody">
          <div className="dhead cim-head">
            <div className="av"><IUp /></div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h2>Import patients</h2>
              <p>{plan ? 'Step 2 of 2 · check what will change' : 'Step 1 of 2 · source, file and columns'}</p>
            </div>
            <button type="button" className="btn cim-tpl" onClick={template}><IDown /> Download template</button>
          </div>

          {!plan && (
            <div className="db">
              <section className="box">
                <div className="bh"><h3>Where the file comes from</h3></div>
                <div className="f">
                  <small>Source</small>
                  <input className="cim-src" value={source} maxLength={60} list="cim-sources" onChange={(e) => setSource(e.target.value)} placeholder="DigiDMS" aria-label="Source" />
                  <datalist id="cim-sources"><option>DigiDMS</option></datalist>
                  <div className="ro">Shown on every contact this file adds or updates: “Import from {label}”.</div>
                </div>
              </section>

              <section className="box">
                <div className="bh"><h3>The file</h3></div>
                <div className={`cim-drop${drag ? ' on' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
                  onDrop={(e) => { e.preventDefault(); setDrag(false); void choose(e.dataTransfer.files?.[0]); }}>
                  <IUp /> {file ? <><b>{file.name}</b> <span className="dim">· {nf(file.sheet.rows.length)} rows · {file.sheet.headers.length} columns</span></> : reading ? <span className="dim">Reading the file…</span> : <b>No file yet</b>}
                  <div className="dim cim-dropnote">
                    Drop a file here or <button type="button" className="linkbtn" onClick={() => inputRef.current?.click()}>choose a file</button> - .xlsx or .csv · nothing is written until you press Import
                  </div>
                  <input ref={inputRef} type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={(e) => void choose(e.target.files?.[0])} />
                </div>
                {fileErr && <div className="err" role="alert" style={{ marginTop: 8 }}>{fileErr}</div>}
              </section>

              {file && (
                <section className="box">
                  <div className="bh"><h3>Match the columns</h3><span className="sp" /><span className="dim bhnote">{targets.length} fields · {matchedCount} columns matched</span></div>
                  <div className="cim-tw">
                    <table className="cim-map">
                      <colgroup><col style={{ width: '24%' }} /><col style={{ width: '34%' }} /><col style={{ width: '18%' }} /><col style={{ width: '24%' }} /></colgroup>
                      <thead><tr><th>Field in VoiceSpectrum</th><th>Column in your file</th><th>Phone type</th><th>First row</th></tr></thead>
                      <tbody>
                        {targets.map((t) => {
                          const i = col(t.key);
                          return (
                            <tr key={t.key}>
                              <td><b>{t.label}</b>{t.custom && <span className="dim"> · Custom</span>}</td>
                              <td>
                                <select value={i} aria-label={`Column for ${t.label}`}
                                  onChange={(e) => { const n = Number(e.target.value); setMap((m) => { const o = { ...m }; for (const k of Object.keys(o)) if (o[k] === n && n >= 0) o[k] = NOT_IN_FILE; o[t.key] = n; return o; });
                                    if (t.phone && n >= 0) { const g = guessPhoneType(file.sheet.headers[n]); if (g) setPtype((p) => ({ ...p, [t.phone!]: g })); } }}>
                                  <option value={NOT_IN_FILE}>— not in the file —</option>
                                  {file.sheet.headers.map((h, hi) => <option key={hi} value={hi}>{h}{usedCols.has(hi) && hi !== i ? ' (used)' : ''}</option>)}
                                </select>
                              </td>
                              <td>
                                {t.phone ? (
                                  <select value={ptype[t.phone]} aria-label={`Type of ${t.label}`} onChange={(e) => setPtype((p) => ({ ...p, [t.phone!]: e.target.value as PhoneType }))}>
                                    {PHONE_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                                  </select>
                                ) : <span className="dim">—</span>}
                              </td>
                              <td><span className="dim">{i >= 0 ? (first[i] || '—') : '—'}</span></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/* v12.10.21 (#1951 C2): one caller type for the file - required when there is no Caller type column or a cell is empty */}
                  <div className={`f cim-ct${needsFileType && !fileType ? ' need' : ''}`} style={{ marginTop: 12 }}>
                    <small>Caller type for this file{needsFileType ? ' (required)' : ''}</small>
                    <select value={fileType} onChange={(e) => setFileType(e.target.value)} aria-label="Caller type for this file">
                      <option value="">{needsFileType ? '— pick one —' : '— from the Caller type column —'}</option>
                      {typeOptions.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
                    </select>
                    <div className="ro">{needsFileType
                      ? (ctCol < 0 ? 'The file has no Caller type column - every contact it adds or updates gets this caller type.' : 'Some rows leave Caller type empty - they get this one.')
                      : 'Read from the Caller type column. The next call that finds a contact sets it again.'}</div>
                  </div>
                  <p className="dim cim-note">Columns matched by their names; change any of them. Phone type is read from the column name (“Mobile phone” → Mobile) - change it if it is wrong.
                    {file.sheet.headers.length > usedCols.size ? ` ${file.sheet.headers.length - usedCols.size} column${file.sheet.headers.length - usedCols.size === 1 ? ' is' : 's are'} not used.` : ' Every column of the file is used - nothing left over.'}</p>
                </section>
              )}
            </div>
          )}

          {plan && (
            <div className="db">
              <section className="box">
                <div className="bh"><h3>Check before importing</h3><span className="sp" /><span className="dim bhnote">{nf(plan.total)} rows · nothing written yet</span></div>
                <div className="cim-counts">
                  <div className="cim-k new"><b>{nf(plan.counts.new)}</b><span className="dim">New contacts</span></div>
                  <div className="cim-k upd"><b>{nf(plan.counts.matched)}</b><span className="dim">Matched - will be updated</span></div>
                  <div className="cim-k shr"><b>{nf(plan.counts.shared)}</b><span className="dim">New contacts on a shared phone</span></div>
                  <div className="cim-k rev"><b>{nf(plan.counts.review)}</b><span className="dim">Needs review - not imported</span></div>
                </div>
                <p className="dim cim-note">How a row finds a contact: same External ID → same phone (Phone 1-4) + same date of birth → same phone + names that sound alike (contact has no date of birth).</p>
              </section>

              {plan.counts.matched > 0 && (
                <section className="box">
                  <div className="bh"><h3>Matched - will be updated</h3><span className="sp" /><span className="dim bhnote">{nf(plan.counts.matched)}{plan.counts.matched > SHOW ? ` · first ${SHOW}` : ''}</span></div>
                  {list('matched').slice(0, SHOW).map((r) => (
                    <div className="crow" key={r.row}><span className="t">
                      <b>{r.systemId} · {r.oldName && r.oldName !== r.name ? <>{r.oldName} → {r.name}</> : r.name}</b><br />
                      <span className="dim">{[r.rule === 'external_id' ? 'External ID' : r.rule === 'phone_dob' ? 'phone + date of birth' : 'phone + names alike', ...(r.changes ?? []).filter((c) => !c.startsWith('Name '))].join(' · ')}</span>
                    </span></div>
                  ))}
                </section>
              )}
              {plan.counts.shared > 0 && (
                <section className="box">
                  <div className="bh"><h3>New contacts on a shared phone</h3><span className="sp" /><span className="dim bhnote">{nf(plan.counts.shared)} · added as new, the phone is linked to both</span></div>
                  {list('shared').slice(0, SHOW).map((r) => (
                    <div className="crow" key={r.row}><span className="t">
                      <b>{r.name}</b> <span className="dim">· {[r.externalId, r.phone ? maskPhone(r.phone) : null].filter(Boolean).join(' · ')}</span><br />
                      <span className="dim">{r.reason}</span>
                    </span></div>
                  ))}
                </section>
              )}
              {(plan.dropped ?? 0) > 0 && (
                <section className="box">{/* v12.10.17.1 (#1787): imported - only the value that could not be read is left out */}
                  <div className="bh"><h3>Imported with a value left out</h3><span className="sp" /><span className="dim bhnote">{nf(plan.dropped ?? 0)} · the rest of the row is imported</span></div>
                  {plan.rows.filter((r) => r.outcome !== 'review' && (r.dropped?.length ?? 0) > 0).slice(0, SHOW).map((r) => (
                    <div className="crow" key={r.row}><span className="t">
                      <b>{r.name || `Row ${r.row}`}</b> <span className="dim">· row {r.row}{r.externalId ? ` · ID ${r.externalId}` : ''}{r.phone ? ` · ${maskPhone(r.phone)}` : ''}</span><br />
                      <span className="dim">{(r.dropped ?? []).join(' · ')}</span>
                    </span></div>
                  ))}
                </section>
              )}
              {plan.counts.review > 0 && (
                <section className="box cim-rev">
                  <div className="bh"><h3>Needs review - not imported</h3><span className="sp" /><button type="button" className="btn sm" onClick={reviewCsv}><IDown />Download these rows</button></div>
                  {list('review').slice(0, SHOW).map((r) => (
                    <div className="crow" key={r.row}>
                      <span className="t"><b>{r.name || `Row ${r.row}`}</b> <span className="dim">· row {r.row}{r.externalId ? ` · ID ${r.externalId}` : ''}{r.phone ? ` · ${maskPhone(r.phone)}` : ''}</span><br />{/* v12.10.17.1 (#1790): "ID 2", never a bare "2" */}
                        <span className="dim">{r.reason}</span></span>
                      <span className="sp" /><span className="cim-skip">Skipped</span>
                    </div>
                  ))}
                  {plan.counts.review > SHOW && <p className="dim cim-note">and {nf(plan.counts.review - SHOW)} more - all of them are in “Download these rows”.</p>}
                  <p className="dim cim-note">Fix these in {label} (or in the file) and import again - a row that matches is updated, never duplicated.</p>
                </section>
              )}
            </div>
          )}
        </div>

        <div className="dfoot">
          {!plan ? (
            <>
              <span className="lbl2">{file ? `${nf(file.sheet.rows.length)} rows read · ${usedCols.size} of ${file.sheet.headers.length} columns matched` : 'Choose a file to start'}</span>
              <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="button" className="btn pri" onClick={() => void check()} disabled={!file || busy || !source.trim() || (needsFileType && !fileType)} title={needsFileType && !fileType ? 'Pick the caller type for this file first' : undefined}>{busy ? 'Checking…' : 'Check the file'}</button>
            </>
          ) : (
            <>
              <span className="lbl2">{nf(toImport)} rows will be imported{plan.counts.review ? ` · ${nf(plan.counts.review)} need${plan.counts.review === 1 ? 's' : ''} review` : ''}</span>
              <button type="button" className="btn" onClick={() => setPlan(null)} disabled={busy}>Back</button>
              <button type="button" className="btn pri" onClick={() => setConfirm(true)} disabled={busy || toImport === 0}>Import {nf(toImport)} rows</button>
            </>
          )}
        </div>
      </aside>
      {confirm && plan && <ConfirmImport plan={plan} source={label} busy={busy} onCancel={() => setConfirm(false)} onConfirm={() => void doImport()} />}
    </>
  );
}

/** The typed confirm (ConfirmDelete pattern, I1): nothing is written until IMPORT is typed. */
function ConfirmImport({ plan, source, busy, onCancel, onConfirm }: { plan: Plan; source: string; busy: boolean; onCancel: () => void; onConfirm: () => void }) {
  const [typed, setTyped] = useState('');
  const ok = typed.trim().toUpperCase() === 'IMPORT';
  const adds = plan.counts.new + plan.counts.shared;
  const n = plan.total - plan.counts.review;
  return (
    <div className="settings-modal-backdrop" role="presentation" style={{ zIndex: 10000 }} onClick={busy ? undefined : onCancel}>
      <section className="settings-modal" role="dialog" aria-modal="true" aria-labelledby="cim-confirm" onClick={(e) => e.stopPropagation()}>
        <h2 id="cim-confirm">Import {nf(n)} patients from {source}?</h2>
        <p><b>{nf(adds)} new contact{adds === 1 ? '' : 's'}</b> {adds === 1 ? 'is' : 'are'} added and <b>{nf(plan.counts.matched)}</b> {plan.counts.matched === 1 ? 'is' : 'are'} updated (name, date of birth, ZIP, phones added, caller type, preferred language).
          Every change is kept in each contact's History.{plan.counts.review ? ` ${nf(plan.counts.review)} row${plan.counts.review === 1 ? ' is' : 's are'} skipped.` : ''}</p>
        <p>Type <b>IMPORT</b> to confirm.</p>
        <input className="settings-input" autoFocus value={typed} placeholder="IMPORT" onChange={(e) => setTyped(e.target.value)} aria-label="Type IMPORT to confirm"
          onKeyDown={(e) => { if (e.key === 'Enter' && ok && !busy) onConfirm(); if (e.key === 'Escape' && !busy) { e.preventDefault(); e.stopPropagation(); onCancel(); } }} />
        <div className="settings-actions">
          <button type="button" className="settings-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="settings-danger cim-go" onClick={onConfirm} disabled={!ok || busy}>{busy ? 'Importing…' : 'Import'}</button>
        </div>
      </section>
    </div>
  );
}
